import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  RefreshCw, CreditCard, Loader2, AlertTriangle, Trash2, ShieldCheck, Zap, CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

const THRESHOLD_OPTIONS = [10, 20, 30, 50, 100];
const MAX_PER_DAY_OPTIONS = [1, 2, 3, 5];

const BRAND_LABEL = {
  visa: "Visa", mastercard: "Mastercard", amex: "Amex",
  discover: "Discover", diners: "Diners", jcb: "JCB", unionpay: "UnionPay",
};

function fmtRefillDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

/**
 * Tarjeta de configuración de la recarga automática de créditos IA (cobro
 * off-session vía Stripe). El backend guarda la tarjeta con un checkout en
 * modo "setup" y cobra sin usuario presente cuando el saldo baja del umbral.
 *
 * Props:
 *  - restaurantId: string
 *  - config: objeto public_config del backend
 *  - packs: catálogo de packs de recarga (top_up_packs)
 *  - onChanged: (newConfig) => void  — sincroniza el estado del padre
 */
export function AutoRefillCard({ restaurantId, config, packs, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [savingCard, setSavingCard] = useState(false);

  if (!config) return null;

  const hasCard = !!config.has_card;
  const pack = packs.find((p) => p.pack_id === config.pack_id);

  const patch = async (update) => {
    setBusy(true);
    try {
      const next = await api.updateAutoRefill(restaurantId, update);
      onChanged(next);
    } catch (e) {
      toast.error(e?.message || "No se pudo actualizar la recarga automática");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveCard = async () => {
    setSavingCard(true);
    try {
      const res = await api.createAutoRefillSetup(restaurantId);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      toast.error("No se pudo iniciar el guardado de tarjeta");
    } catch (e) {
      toast.error(e?.message || "No se pudo iniciar el guardado de tarjeta");
    }
    setSavingCard(false);
  };

  const handleRemoveCard = async () => {
    setBusy(true);
    try {
      const next = await api.removeAutoRefillCard(restaurantId);
      onChanged(next);
      toast.success("Tarjeta eliminada y recarga automática desactivada");
    } catch (e) {
      toast.error(e?.message || "No se pudo quitar la tarjeta");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = (checked) => {
    if (checked && !hasCard) {
      toast.info("Guarda primero una tarjeta para activar la recarga automática");
      return;
    }
    patch({ enabled: checked });
  };

  const brand = config.card_brand ? (BRAND_LABEL[config.card_brand] || config.card_brand) : "Tarjeta";

  return (
    <div className="bg-card border rounded-xl p-5" id="autorefill-section" data-testid="autorefill-card">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            Recarga automática de créditos
            {config.enabled && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold uppercase tracking-wide" data-testid="autorefill-active-badge">
                Activa
              </span>
            )}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Cuando tu saldo baje del umbral, cobramos tu tarjeta y añadimos créditos sin interrumpir la IA.
          </p>
        </div>
      </div>

      {/* Error banner (cobro fallido → recarga desactivada) */}
      {config.last_error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2 text-red-400 text-xs" data-testid="autorefill-error-banner">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{config.last_error}</span>
        </div>
      )}

      {/* Success receipt (última recarga automática correcta) */}
      {!config.last_error && config.last_refill_at && (
        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-start gap-2 text-emerald-400 text-xs" data-testid="autorefill-success-banner">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Última recarga automática
            {config.last_refill_credits ? <> de <strong>+{config.last_refill_credits} créditos</strong></> : ""}
            {config.last_refill_amount ? ` (${config.last_refill_amount} €)` : ""}
            {" "}el {fmtRefillDate(config.last_refill_at)}.
          </span>
        </div>
      )}

      {/* Saved card / save card */}
      <div className="rounded-lg border bg-muted/30 p-3 mb-4">
        {hasCard ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-background border flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium" data-testid="autorefill-card-info">
                  {brand} ···· {config.card_last4 || "····"}
                </p>
                <p className="text-[10px] text-muted-foreground">Tarjeta guardada de forma segura en Stripe</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline" className="h-8 text-xs rounded-full"
                disabled={savingCard || busy}
                onClick={handleSaveCard}
                data-testid="autorefill-change-card-btn"
              >
                {savingCard ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Cambiar"}
              </Button>
              <Button
                size="sm" variant="ghost"
                className="h-8 text-xs rounded-full text-red-400 hover:text-red-500 hover:bg-red-500/10"
                disabled={busy || savingCard}
                onClick={handleRemoveCard}
                data-testid="autorefill-remove-card-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-sage flex-shrink-0" />
              Guarda una tarjeta para habilitar la recarga automática.
            </div>
            <Button
              size="sm" className="h-8 text-xs rounded-full gap-1.5"
              disabled={savingCard}
              onClick={handleSaveCard}
              data-testid="autorefill-save-card-btn"
            >
              {savingCard
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirigiendo...</>
                : <><CreditCard className="w-3.5 h-3.5" /> Guardar tarjeta</>}
            </Button>
          </div>
        )}
      </div>

      {/* Toggle */}
      <div className="flex items-center justify-between py-1">
        <div>
          <p className="text-sm font-medium">Activar recarga automática</p>
          <p className="text-[10px] text-muted-foreground">Requiere una tarjeta guardada</p>
        </div>
        <Switch
          checked={config.enabled}
          disabled={!hasCard || busy}
          onCheckedChange={handleToggle}
          data-testid="autorefill-toggle"
        />
      </div>

      {/* Config (only when a card exists) */}
      {hasCard && (
        <div className="mt-4 space-y-4 border-t pt-4">
          {/* Pack selector */}
          <div>
            <p className="text-xs font-medium mb-2 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-muted-foreground" /> Pack a recargar
            </p>
            <div className="grid grid-cols-3 gap-2">
              {packs.map((p) => {
                const active = p.pack_id === config.pack_id;
                return (
                  <button
                    key={p.pack_id}
                    onClick={() => !active && patch({ pack_id: p.pack_id })}
                    disabled={busy}
                    data-testid={`autorefill-pack-${p.pack_id}`}
                    className={`rounded-lg border p-2.5 text-center transition-colors disabled:opacity-60 ${
                      active
                        ? "ring-2 ring-primary border-primary bg-primary/5"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    <span className="block text-sm font-bold">{p.credits}</span>
                    <span className="block text-[10px] text-muted-foreground">créditos</span>
                    <span className="block text-xs font-medium mt-0.5">{p.price} €</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Threshold */}
          <div>
            <p className="text-xs font-medium mb-2">Recargar cuando el saldo baje de</p>
            <div className="flex flex-wrap gap-1.5">
              {THRESHOLD_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => n !== config.threshold && patch({ threshold: n })}
                  disabled={busy}
                  data-testid={`autorefill-threshold-${n}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors disabled:opacity-60 ${
                    config.threshold === n
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:bg-muted"
                  }`}
                >
                  {n} créditos
                </button>
              ))}
            </div>
          </div>

          {/* Max per day */}
          <div>
            <p className="text-xs font-medium mb-2">
              Recargas máximas por día
              <span className="text-[10px] text-muted-foreground font-normal ml-1">(tope de seguridad)</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {MAX_PER_DAY_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => n !== config.max_per_day && patch({ max_per_day: n })}
                  disabled={busy}
                  data-testid={`autorefill-maxperday-${n}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors disabled:opacity-60 ${
                    config.max_per_day === n
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:bg-muted"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Summary line */}
          <p className="text-[11px] text-muted-foreground">
            Se cobrarán <strong>{pack ? `${pack.price} €` : "el pack seleccionado"}</strong>
            {pack ? ` (+${pack.credits} créditos)` : ""} automáticamente cuando el saldo baje de{" "}
            <strong>{config.threshold}</strong> créditos, hasta {config.max_per_day} ve{config.max_per_day > 1 ? "ces" : "z"} al día.
            {config.refills_today > 0 && ` Hoy: ${config.refills_today} recarga${config.refills_today > 1 ? "s" : ""}.`}
          </p>
        </div>
      )}
    </div>
  );
}
